import React, { useState, useEffect } from 'react';
import { chatService, StaffChatThread } from '../../services/chatService';
import { ChatThread, ThreadFilters } from '../../types/chat';
import { apiErrorMessage } from '../../utils/apiError';

interface ThreadListProps {
  onSelectThread: (thread: ChatThread) => void;
  selectedThreadId?: string;
  onSelectStaffThread?: (thread: StaffChatThread, label: string) => void;
  selectedStaffThreadId?: string;
  refreshKey?: number;
}

function staffChannelLabel(thread: StaffChatThread): string {
  const other =
    thread.requester_role === 'cns' ? thread.recipient_role : thread.requester_role;
  switch (other) {
    case 'ont':
      return 'ONT — Office National du Tourisme';
    case 'anr':
      return 'ANR — Agence Nationale de Renseignements';
    case 'admin':
      return 'Support plateforme';
    case 'cns':
      return 'CNS';
    default:
      return (other || 'Interne').toUpperCase();
  }
}

const ThreadList: React.FC<ThreadListProps> = ({
  onSelectThread,
  selectedThreadId,
  onSelectStaffThread,
  selectedStaffThreadId,
  refreshKey = 0,
}) => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [staffThreads, setStaffThreads] = useState<StaffChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ThreadFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, refreshKey]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      setError(null);
      const [hotelData, staffData] = await Promise.all([
        chatService.getThreads(filters),
        chatService.getStaffThreads().catch(() => [] as StaffChatThread[]),
      ]);
      setThreads(Array.isArray(hotelData) ? hotelData : []);
      setStaffThreads(Array.isArray(staffData) ? staffData : []);
    } catch (err: unknown) {
      console.error('Error loading threads:', err);
      const msg =
        (err as { response?: { data?: unknown } })?.response?.data != null
          ? apiErrorMessage((err as { response: { data: unknown } }).response.data, 'Erreur de chargement')
          : err instanceof Error
            ? err.message
            : 'Erreur lors du chargement des conversations';
      setError(msg);
      setThreads([]);
      setStaffThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters({ ...filters, search: searchQuery });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value || undefined });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `${minutes} min`;
    if (hours < 24) return `${hours} h`;
    if (days < 7) return `${days} j`;
    return date.toLocaleDateString('fr-FR');
  };

  const openCount = threads.filter(
    (thread) => thread.status === 'open' || thread.status === 'in_progress'
  ).length;
  const unreadCount = threads.reduce(
    (total, thread) => total + (thread.unread_count_cns || 0),
    0
  );

  return (
    <div className="flex flex-col h-full bg-white border-r">
      <div className="p-4 border-b space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Conversations</h2>
          <p className="mt-1 text-sm text-gray-500">
            Hôtels et canaux inter-agences (ONT / ANR / Support).
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
            <div className="text-lg font-semibold text-blue-900">{openCount}</div>
            <div className="text-xs text-blue-700">hôtels actives</div>
          </div>
          <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
            <div className="text-lg font-semibold text-red-900">{unreadCount}</div>
            <div className="text-xs text-red-700">non lues</div>
          </div>
        </div>

        <div className="flex space-x-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher hôtel, client, référence..."
            className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="open">Ouvert</option>
            <option value="in_progress">En cours</option>
            <option value="resolved">Résolu</option>
            <option value="closed">Fermé</option>
          </select>

          <select
            value={filters.priority || ''}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Toutes priorités</option>
            <option value="normal">Normale</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="m-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
            <button onClick={loadThreads} className="ml-2 underline font-medium">
              Réessayer
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {staffThreads.length > 0 && (
              <div className="border-b">
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50">
                  Inter-agences ({staffThreads.length})
                </div>
                {staffThreads.map((st) => (
                  <div
                    key={st.id}
                    onClick={() => onSelectStaffThread?.(st, staffChannelLabel(st))}
                    className={`p-4 border-b cursor-pointer hover:bg-indigo-50 transition-colors ${
                      selectedStaffThreadId === st.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">{staffChannelLabel(st)}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Canal sécurisé institutionnel</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(st.status || 'open')}`}>
                        {(st.status || 'open').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      {formatTime(st.updated_at || st.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Hôtels ({threads.length})
            </div>

            {threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-gray-500 p-6">
                <svg className="w-14 h-14 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p className="text-sm font-medium text-gray-700">Aucune conversation hôtel</p>
                <p className="mt-1 text-xs text-gray-500 text-center">
                  Utilisez « Contacter un Hôtel » pour démarrer, ou attendez les demandes entrantes.
                </p>
              </div>
            ) : (
              threads.map((thread) => (
                <div
                  key={thread.id}
                  onClick={() => onSelectThread(thread)}
              className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedThreadId === thread.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
              } ${(thread.unread_count_cns || 0) > 0 ? 'bg-red-50' : ''}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                      <h3 className={`font-semibold text-gray-800 ${(thread.unread_count_cns || 0) > 0 ? 'font-bold' : ''}`}>
                          {thread.hotel_name || 'Unknown Hotel'}
                        </h3>
                        {thread.priority === 'urgent' && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                            URGENT
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        Client: {thread.guest_name || 'Non renseigné'} (
                        {thread.guest_document || 'N/A'})
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(thread.status)}`}>
                        {(thread.status || 'open').replace('_', ' ')}
                      </span>
                      {(thread.unread_count_cns || 0) > 0 && (
                        <div className="mt-1">
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                            {thread.unread_count_cns}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {thread.last_message_preview && (
                    <p className="text-sm text-gray-600 truncate mb-1">{thread.last_message_preview}</p>
                  )}

                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>
                      {thread.assigned_to_name
                        ? `Assigné à ${thread.assigned_to_name}`
                        : 'Non assigné'}
                    </span>
                    <span>{formatTime(thread.last_message_at || thread.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ThreadList;
